"use client";

import { create } from "zustand";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { hasPublicSupabaseEnv } from "@/lib/env";
import { authDebug } from "@/lib/auth-debug";
import { createSupabaseBrowserClient } from "@/lib/supabase/browser";

export type AuthStatus = "idle" | "initializing" | "authenticated" | "anonymous" | "error";

type SignUpInput = {
  email: string;
  password: string;
  fullName?: string;
  accountRole?: "user" | "business";
  emailRedirectTo?: string;
};

type AuthState = {
  session: Session | null;
  user: User | null;
  accessToken: string | null;
  status: AuthStatus;
  initialized: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<Session | null>;
  signUp: (input: SignUpInput) => Promise<{ session: Session | null; user: User | null }>;
  signOut: () => Promise<void>;
  refreshSession: (force?: boolean) => Promise<Session | null>;
  getAccessToken: () => Promise<string | null>;
};

let initializePromise: Promise<void> | null = null;
let refreshPromise: Promise<Session | null> | null = null;
let authUnsubscribe: (() => void) | null = null;

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Authentication failed.";
}

function sessionState(session: Session | null) {
  return {
    session,
    user: session?.user ?? null,
    accessToken: session?.access_token ?? null,
    status: session ? "authenticated" as const : "anonymous" as const,
    error: null
  };
}

function expiresSoon(session: Session | null) {
  if (!session?.expires_at) return false;
  return session.expires_at * 1000 <= Date.now() + 60_000;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  accessToken: null,
  status: "idle",
  initialized: false,
  error: null,

  initialize: async () => {
    if (get().initialized) return;
    if (initializePromise) return initializePromise;

    initializePromise = (async () => {
      if (!hasPublicSupabaseEnv()) {
        set({
          session: null,
          user: null,
          accessToken: null,
          status: "anonymous",
          initialized: true,
          error: null
        });
        return;
      }

      set({ status: "initializing", error: null });
      try {
        const supabase = createSupabaseBrowserClient();
        if (!authUnsubscribe) {
          const { data: listener } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
            authDebug("[auth] state changed", {
              event,
              hasSession: Boolean(session),
              hasUser: Boolean(session?.user?.id)
            });
            set({
              ...sessionState(session),
              initialized: true
            });
          });
          authUnsubscribe = () => listener.subscription.unsubscribe();
        }

        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;

        set({
          ...sessionState(data.session),
          initialized: true
        });

      } catch (error) {
        set({
          session: null,
          user: null,
          accessToken: null,
          status: "error",
          initialized: true,
          error: errorMessage(error)
        });
      }
    })().finally(() => {
      initializePromise = null;
    });

    return initializePromise;
  },

  signIn: async (email, password) => {
    set({ status: "initializing", error: null });
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const result = await response.json().catch(() => null) as { success?: boolean; message?: string } | null;
      if (!response.ok || !result?.success) {
        throw new Error(result?.message ?? "Sign in failed.");
      }

      return await get().refreshSession(true);
    } catch (error) {
      set({ status: "error", error: errorMessage(error) });
      throw error;
    }
  },

  signUp: async ({ email, password, fullName, accountRole = "user", emailRedirectTo }) => {
    set({ status: "initializing", error: null });
    try {
      const supabase = createSupabaseBrowserClient();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            full_name: fullName?.trim() || null,
            account_role: accountRole
          }
        }
      });
      if (error) throw error;
      set({
        ...sessionState(data.session),
        initialized: true
      });
      return data;
    } catch (error) {
      set({ status: "error", error: errorMessage(error) });
      throw error;
    }
  },

  signOut: async () => {
    try {
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        redirect: "manual"
      });
    } finally {
      set({
        session: null,
        user: null,
        accessToken: null,
        status: "anonymous",
        initialized: true,
        error: null
      });
    }
  },

  refreshSession: async (force = false) => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        const supabase = createSupabaseBrowserClient();
        const current = await supabase.auth.getSession();
        if (current.error) throw current.error;

        if (!current.data.session) {
          set({
            ...sessionState(null),
            initialized: true
          });
          return null;
        }

        if (!force && !expiresSoon(current.data.session)) {
          set({
            ...sessionState(current.data.session),
            initialized: true
          });
          return current.data.session;
        }

        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.error) throw refreshed.error;
        set({
          ...sessionState(refreshed.data.session),
          initialized: true
        });
        return refreshed.data.session;
      } catch (error) {
        set({
          session: null,
          user: null,
          accessToken: null,
          status: "error",
          initialized: true,
          error: errorMessage(error)
        });
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  },

  getAccessToken: async () => {
    if (!get().initialized) await get().initialize();
    const current = get().session;
    if (!current) return null;
    if (expiresSoon(current)) {
      const refreshed = await get().refreshSession(true);
      return refreshed?.access_token ?? null;
    }
    return current.access_token ?? null;
  }
}));

export function resetAuthStoreForTests() {
  authUnsubscribe?.();
  authUnsubscribe = null;
  initializePromise = null;
  refreshPromise = null;
  useAuthStore.setState({
    session: null,
    user: null,
    accessToken: null,
    status: "idle",
    initialized: false,
    error: null
  });
}
